<template>
  <v-hover v-slot="{ hover }">
    <v-list-item :style="!isOpen ? 'opacity: 0.6;' : ''" v-on="hasClickListener ? { click: () => $emit('click') } : {}">
      <slot name="icon"></slot>

      <v-list-item-action v-if="hasActionListener">
        <slot name="action"></slot>
      </v-list-item-action>

      <v-list-item-content>
        <v-list-item-subtitle v-if="!small" class="text-xs">
          <tooltip
            v-if="!hasActionListener"
            :text="$t('partner.PartnerReportDetailChangeProgress.' + item.progressStatus)"
          >
            <v-avatar class="mr-1 white--text" :color="color" :elevation="0" size="8" />
          </tooltip>
          <span>
            {{ date }}
          </span>
        </v-list-item-subtitle>

        <v-list-item-title class="text-truncate" :class="isClosed ? 'text-decoration-line-through' : ''">
          <a v-if="hasOpenDetailListener" @click.stop="emitOpenDetail">
            <span v-if="item.numberplate"> {{ item.numberplate }} </span>
            <span v-if="item.title && item.numberplate">-</span>
            <span v-if="item.title">
              {{ item.title }}
            </span>
            <span v-if="item.externalId">[{{ item.externalId }}]</span>
          </a>

          <div v-else>
            <span v-if="item.title && item.title !== item.numberplate">
              {{ item.numberplate }} - {{ item.title }}
            </span>
            <span v-else> {{ item.numberplate }} </span>
            <span v-if="item.externalId">({{ item.externalId }})</span>
          </div>
        </v-list-item-title>

        <v-list-item-subtitle v-if="!small" class="text-truncate text-xs">
          <span v-if="item.customerName">
            <v-icon x-small>mdi-account</v-icon>
            {{ item.customerName }}
          </span>
          <span v-if="item.companyName">
            <v-icon x-small>mdi-domain</v-icon>
            {{ item.companyName }}
          </span>
          <span v-if="item.customerContact">
            <v-icon x-small>mdi-phone</v-icon>
            {{ item.customerContact.phone }}
          </span>
          <span v-if="item.customerContact">
            <v-icon x-small>mdi-email</v-icon>
            {{ item.customerContact.email }}
          </span>

          <span v-if="imageCount">
            <v-icon x-small>mdi-image-multiple</v-icon>
            {{ imageCount }}
          </span>

          <span v-if="item.damage.length > 0">
            <v-icon x-small>mdi-car-brake-alert</v-icon>
            <span v-for="d in item.damage" :key="item.id + d">
              {{ $t(`components.partner.PartnerReportDetail.PartnerReportUpdateReportDialog.report.damages.${d}`) }},
            </span>
          </span>

          <span class="text-truncate">
            {{ item.message }}
          </span>
        </v-list-item-subtitle>
      </v-list-item-content>
      <Can :I="ActionEnum.UPDATE" :a="ResourceEnum.REPORT" :field="partnerId">
        <v-list-item-action class="mx-0 px-0" v-if="confirmable">
          <tooltip :text="$t('common.BackendResourceEnum.click.tooltip.report')" v-if="isConfirmable">
            <v-flex>
              <v-btn
                v-if="hover || confirmablePersistant"
                text
                color="success"
                elevation="0"
                small
                @click="confirm"
                :loading="loadingConfirm"
              >
                {{ $t(`common.BackendResourceEnum.click.report`) }}
                <v-icon small class="pl-1"> mdi-check </v-icon>
              </v-btn>
            </v-flex>
          </tooltip>
        </v-list-item-action>
      </Can>
    </v-list-item>
  </v-hover>
</template>

<script lang="ts">
import { detailedDateWithDay } from "@/lib/utility/date-helper";
import { GoToHelper } from "@/lib/utility/goToHelper";
import { handleError } from "@/lib/utility/handleError";
import { IReport } from "@/models/report.entity";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { ResourceEnum } from "@/store/enum/authResourceEnum";
import { ProgressStatusEnum } from "@/store/enum/partner/progress.status.enum";
import { reportStateMap, reportStatusIconMap } from "@/store/mapper/report-icon.map";
import { Component, Prop } from "vue-property-decorator";
import BaseRef from "./BaseRef.vue";
import Tooltip from "./tooltip.vue";
import { IRefDetail } from "./RefsList.vue";

@Component({
  components: { Tooltip }
})
export default class RefsReport extends BaseRef<IReport> implements IRefDetail<IReport> {
  @Prop()
  item!: IReport;

  @Prop({ default: false })
  small!: boolean;

  @Prop({ default: false })
  confirmable!: boolean;

  @Prop({ default: false })
  confirmablePersistant!: boolean;

  loadingConfirm = false;

  ProgressStatusEnum = ProgressStatusEnum;

  get ActionEnum() {
    return ActionEnum;
  }

  get ResourceEnum() {
    return ResourceEnum;
  }

  get partnerId() {
    return this.item.partnerId;
  }

  get date() {
    if (this.item?.timestamps?.created) {
      const locale = this.$t("utility.toLocalDateString").toString() || "de-de";
      return detailedDateWithDay(this.item?.timestamps?.created, locale);
    }
    return this.$t("sign.DocumentDetail.noDate");
  }

  get icon() {
    const status = this.item.progressStatus;
    if (!status) return "";
    return reportStatusIconMap.get(status as ProgressStatusEnum) || "";
  }

  get color() {
    return reportStateMap.get(this.item.progressStatus as ProgressStatusEnum)?.color || "blue-grey lighten-4";
  }

  get isOpen() {
    return (
      this.item.progressStatus === ProgressStatusEnum.NEW || this.item.progressStatus === ProgressStatusEnum.IN_PROGRESS
    );
  }

  get isClosed() {
    return (
      this.item.progressStatus === ProgressStatusEnum.DELETED ||
      this.item.progressStatus === ProgressStatusEnum.FINISHED
    );
  }

  get imageCount() {
    if (!this.item.images) {
      return 0;
    }

    let counter = 0;
    for (const image of Object.values(this.item.images)) {
      counter += image.length;
    }

    return counter;
  }

  get isConfirmable() {
    return (
      this.item.progressStatus !== ProgressStatusEnum.FINISHED &&
      this.item.progressStatus !== ProgressStatusEnum.DELETED
    );
  }

  emitOpenDetail() {
    this.$emit("openDetail", this.item);
  }

  confirm() {
    this.confirmAsync();
    this.$emit("confirm", this.item);
  }

  goToDetail() {
    new GoToHelper(this.$router).goToReportDetail(this.item.id, this.item.partnerId, true);
  }

  async confirmAsync() {
    try {
      this.loadingConfirm = true;
      this.item.updateProgressStatus(ProgressStatusEnum.FINISHED);

      this.$toast.success(this.$t("common.BackendResourceEnum.click.success.report"));
    } catch (error) {
      handleError(error);
    } finally {
      this.loadingConfirm = false;
    }
  }
}
</script>
